import React, { useState, useEffect, useMemo } from 'react';
import { Helmet as MetaTags } from 'react-helmet';
import { v4 as uuidv4 } from 'uuid';
import memoizedComposeFlowListData, {
  FlowListData,
} from '~/components/page/Automation/v2/util/composeFlowListData';
import {
  FlowData___ActionFragment,
  FlowFieldsV2Fragment,
  GetFlowsV2Query,
  GetFlowsV2QueryVariables,
  SortDirection,
  useGetFlowsV2Query,
} from '~/graphql/types';
import TEST_ID from './index.testid';
import ContentContainerDefault, {
  MAX_CONTAINER_WIDTH,
} from '~/components/molecule/ContentContainer';
import FlowListTable, { type SelectedFlow } from './components/FlowListTable';
import flowListColumns from './components/FlowListColumns';
import InfiniteScroll from '~/components/molecule/InfiniteScroll';
import {
  globalHistory,
  navigate,
  RouteComponentProps,
  useLocation,
  type WindowLocation,
} from '@gatsbyjs/reach-router';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import Loading from '~/components/atom/Loading';
import useSortSettings from '~/hooks/useSortSettings';
import useResetFlowState from '../Builder/hooks/useResetFlowState';
import { LINK_PREFIX } from '../Wizard';
import { isNil, prop, uniqBy } from 'ramda';
import { useSetRecoilState } from 'recoil';
import { flowActions } from '../../state/actions';
import getFlowActionsToClientActions from '~/components/page/Automation/v2/util/getFlowActionsToClientActions';
import BndlLoading from '~/components/atom/BndlLoading';
import generateMapsCombined from '../UpdateAction/components/Selector/utils/generateMapsCombined';
import EmptyStateComponent from '~/components/template/EmptyStateComponent';
import createPageTitle from '~/util/createPageTitle';
import useRowSelect from '~/components/organism/Table/hooks/useRowSelect';
import FlowListToolbar from './components/FlowListToolbar';
import useTrackEvent from '~/hooks/useTrackEvent';
import MasterAccountWarning from './components/MasterAccountWarning';
import useAvailableEmailsToSendFrom from '~/hooks/useAvailableEmailsToSendFrom';
import { isSuccessEmailSyncStatus } from '~/util/constants';
import useErrorReporter from '~/hooks/useErrorReporter';
import getTriggerLabels from './utils/getTriggerLabels';
import type { IBuilderContext } from '../Builder/context';
import isEmailRequiredAction from './utils/getTriggerLabels/isEmailRequiredAction';

type WindowState = {
  /** Do not fetch the flow list when navigating to this page */
  noRefetch?: boolean;
};

const text = {
  pageTitle: 'Automation',
  title: 'Automation',
  addFlowLabel: 'Nieuwe flow',
  importFlowLabel: 'Flow importeren',
  noFlows: 'Je hebt nog geen flows',
  emptyStateDescription:
    'Benader leads en relaties met gepersonaliseerde marketing campagnes.',
  notification:
    'Maak kennis met de nieuwe flowbuilder v2! Al je bestaande flows zijn beschikbaar en vind je onderaan deze pagina. Binnenkort wordt de bestaande flowbuilder vervangen. Al je bestaande flows worden dan automatisch overgezet.',
  deleteAllFlows: 'Delete all flows',
  notAvailable: 'Waarde is niet (meer) beschikbaar',
};

export const FLOW_LIST_LIMIT = 30;

const FlowList: React.FC<RouteComponentProps> = () => {
  const errorReporter = useErrorReporter();
  const location = useLocation() as WindowLocation<WindowState>;
  const account = useCurrentAccount();
  const trackEvent = useTrackEvent();
  const availableEmailsToSendFrom = useAvailableEmailsToSendFrom();
  const hasAvailableEmailToSendFrom =
    availableEmailsToSendFrom.filter(
      email => email.syncState && isSuccessEmailSyncStatus(email.syncState),
    ).length > 0;

  const [version, setVersion] = useState<string>(uuidv4());

  const [sortSettings, updateSortSettings] =
    useSortSettings<Array<string>>('flowSortSettings');

  const resetFlowState = useResetFlowState();

  useEffect(() => {
    resetFlowState();
  }, [resetFlowState]);

  const baseVariables = useMemo(
    () => ({
      accountId: account.id,
      limit: FLOW_LIST_LIMIT,
    }),
    [account],
  );

  const { data, fetchMore, networkStatus, loading, error, updateQuery } =
    useGetFlowsV2Query({
      variables: baseVariables,
      fetchPolicy: location.state?.noRefetch ? 'cache-first' : 'network-only',
    });

  const selectionProps = useRowSelect<SelectedFlow>(item => item.flowId);
  const { itemsSelected, deselectAllItems } = selectionProps;

  const [tableData, setTableData] = useState<Array<FlowListData>>([]);

  const firstItem = data?.getFlowsV2.items[0];
  const memoizedIBuilderContext: Omit<
    IBuilderContext,
    'zapierTriggers' | 'offices' | 'users'
  > | null = useMemo(() => {
    if (!data?.getFlowData) return null;

    const {
      subjectMap,
      conditionMap,
      subjectToConditionMap,
      directoryMap,
      primitiveInputMap,
      instanceMap,
    } = generateMapsCombined(data.getFlowData);

    const availableActions =
      data.getFlowData.availableActions.filter(
        (action): action is FlowData___ActionFragment => !isNil(action),
      ) ?? [];

    const availableFlows = data.getFlowsV2.items.map(flow => ({
      id: flow.id,
      name: flow.name,
    }));

    return {
      flowBlueprintId: firstItem?.id ?? '',
      opts: {
        subjectMap,
        conditionMap,
        subjectToConditionMap,
        directoryMap,
      },
      instanceMap,
      primitiveInputMap,
      primitiveInput: data.getFlowData.primitiveInput,
      primitiveListInput: data.getFlowData.primitiveListInput,
      superSubjects: data.getFlowData.superSubjects,
      subjects: data.getFlowData.subjects,
      instances: data.getFlowData.instance,
      accountId: account.id,
      availableActions,
      initialFlow: {
        flowName: firstItem?.name ?? 'no name',
        flowDescription: firstItem?.description ?? 'no description',
        enabled: firstItem?.enabled ?? true,
        maximumFlowRun: firstItem?.maximumFlowRun ?? 0,
        actions: [],
      },
      availableFlows,
    };
  }, [account.id, data?.getFlowData, data?.getFlowsV2.items, firstItem]);

  useEffect(() => {
    if (data?.getFlowsV2 && memoizedIBuilderContext) {
      const items =
        data?.getFlowsV2.items.map(item => ({
          ...item,
          isMissingEmailToSendFrom:
            !hasAvailableEmailToSendFrom &&
            item.Actions.some(({ __typename }) =>
              isEmailRequiredAction(__typename),
            ),
        })) || [];

      const triggerLabels = getTriggerLabels(
        data.getFlowsV2.items,
        memoizedIBuilderContext,
      );

      setTableData(
        memoizedComposeFlowListData(
          items,
          { ...baseVariables, nextToken: data?.getFlowsV2?.nextToken },
          version,
          sortSettings,
          triggerLabels,
        ),
      );
    }
  }, [
    baseVariables,
    data?.getFlowsV2,
    memoizedIBuilderContext,
    sortSettings,
    version,
    hasAvailableEmailToSendFrom,
    errorReporter,
  ]);

  const nextToken = data?.getFlowsV2?.nextToken;

  const loadMore = () =>
    fetchMore({
      variables: {
        ...baseVariables,
        nextToken: nextToken || null,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        const prevData = previousResult.getFlowsV2;
        const newData = fetchMoreResult.getFlowsV2;

        return {
          ...previousResult,
          getFlowsV2: {
            ...prevData,
            items: [...prevData.items, ...(newData.items || [])],
            nextToken: newData.nextToken,
          },
        };
      },
    });

  const setActions = useSetRecoilState(flowActions);

  useEffect(() => {
    if (data && data.getFlowsV2.items.length !== 0) {
      const startActions = data.getFlowsV2.items
        .map(({ StartAction }) => StartAction)
        .filter(x => x) as FlowFieldsV2Fragment['Actions'];

      const newActions =
        startActions.length > 0
          ? getFlowActionsToClientActions(startActions)
          : [];

      setActions(prev => uniqBy(prop('id'), [...prev, ...newActions]));
    }
  }, [data, data?.getFlowsV2.items, setActions]);

  useEffect(
    () =>
      globalHistory.listen(() => {
        setActions(() => []);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (!data) return <BndlLoading />;

  const onSuccessfulDelete = (flowId: string) => {
    /** ----- TRACKING: flow_deleted ----- */
    trackEvent({
      eventName: 'flow_deleted',
      parameters: { flow_id: flowId },
    });
    updateQuery(getFlowsUpdateAfterRemoveQueryFunction(flowId));
    const nextID = uuidv4();
    setVersion(nextID);
  };

  const onSuccessfulEnabledChange = (flowId: string, newStatus: boolean) => {
    updateQuery(getFlowsUpdateAfterUpdatingStatusFunction(newStatus, flowId));
    setVersion(uuidv4());
  };

  const hasErrors = tableData.some(flow => !isNil(flow.errorMessage));

  return (
    <ContentContainerDefault maxContentWidth={MAX_CONTAINER_WIDTH}>
      <MetaTags>
        <title>{createPageTitle(text.pageTitle)}</title>
      </MetaTags>

      <MasterAccountWarning />

      <FlowListToolbar
        onSuccessfulDelete={onSuccessfulDelete}
        onSuccessfulEnabledChange={onSuccessfulEnabledChange}
        selectedItems={itemsSelected}
        deselectAllItems={deselectAllItems}
      />

      {loading ? (
        <Loading />
      ) : tableData.length === 0 ? (
        <EmptyStateComponent
          dataTestId={TEST_ID.EMPTY_STATE}
          header={text.noFlows}
          description={text.emptyStateDescription}
          buttonProps={{
            label: text.addFlowLabel,
            onClick: () => navigate(`${LINK_PREFIX}/v2/`),
          }}
        />
      ) : (
        <InfiniteScroll
          fetchMoreFn={loadMore}
          hasMore={nextToken != null}
          data-testid={TEST_ID.CONTAINER}
        >
          <FlowListTable
            selectionProps={selectionProps}
            columns={flowListColumns(
              (key: Array<string>, direction: SortDirection) => {
                updateSortSettings({
                  sortField: key,
                  sortDirection: direction,
                });
                setVersion(uuidv4());
              },
              version,
              hasErrors,
            )}
            data={tableData}
            loading={loading}
            networkStatus={networkStatus}
            error={error != null}
            onSuccessfulDelete={onSuccessfulDelete}
            onSuccessfulEnabledChange={onSuccessfulEnabledChange}
          />
        </InfiniteScroll>
      )}
    </ContentContainerDefault>
  );
};

const getFlowsUpdateAfterRemoveQueryFunction =
  (
    flowId: string,
  ): ((
    previousQueryResult: GetFlowsV2Query,
    options: { variables: GetFlowsV2QueryVariables },
  ) => GetFlowsV2Query) =>
  previousResult => {
    const newList = previousResult.getFlowsV2.items.filter(
      flow => flow.id !== flowId,
    );

    return {
      ...previousResult,
      getFlowsV2: {
        ...previousResult.getFlowsV2,
        items: newList,
      },
    };
  };

const getFlowsUpdateAfterUpdatingStatusFunction =
  (
    newStatus: boolean,
    flowId: string,
  ): ((
    previousQueryResult: GetFlowsV2Query,
    options: { variables: GetFlowsV2QueryVariables },
  ) => GetFlowsV2Query) =>
  previousResult => {
    const newList = previousResult.getFlowsV2.items.map(flow => {
      if (flow.id === flowId) {
        return {
          ...flow,
          enabled: newStatus,
        };
      }

      return flow;
    });

    return {
      ...previousResult,
      getFlowsV2: {
        ...previousResult.getFlowsV2,
        items: newList,
      },
    };
  };

export default FlowList;
